import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaCamera,
    FaCheck,
    FaCheckCircle,
    FaChild,
    FaMapMarkerAlt,
    FaSave,
    FaTrash,
    FaUser
} from 'react-icons/fa';

import '../css/EditProfile.css';

const MAX_PHOTOS = 6;
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;

function EditProfile() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const locationTimerRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        age: '',
        location: '',
        city: '',
        state: '',
        country: '',
        latitude: null,
        longitude: null,
        maritalStatus: '',
        gender: '',
        hasChildren: false,
        skinTone: '',
        bio: '',
        profilePicture: [],
        imagesToRemove: []
    });

    const [existingImages, setExistingImages] = useState([]);
    const [newImagePreviews, setNewImagePreviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [locationMessage, setLocationMessage] = useState('');

    const currentLanguage = useMemo(
        () => (i18n.language?.startsWith('fr') ? 'fr' : 'en'),
        [i18n.language]
    );

    const authorizationHeader = useMemo(() => {
        const token = localStorage.getItem('authToken');
        if (!token) return null;
        return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }, []);

    const updateFormData = (changes) => {
        setFormData((previous) => ({ ...previous, ...changes }));
    };

    useEffect(() => {
        if (!authorizationHeader) {
            setLoading(false);
            setErrorMessage(
                t('editProfile.authenticationRequired', 'Vous devez être connecté pour modifier votre profil.')
            );
            return;
        }

        const controller = new AbortController();

        const loadProfile = async () => {
            setLoading(true);
            setErrorMessage('');

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/users/me`,
                    {
                        headers: { Authorization: authorizationHeader },
                        signal: controller.signal
                    }
                );

                const data = response.data || {};

                setFormData((previous) => ({
                    ...previous,
                    name: data.name || '',
                    age: data.age || '',
                    location: data.location || '',
                    city: data.city || '',
                    state: data.state || '',
                    country: data.country || '',
                    latitude: data.latitude ?? null,
                    longitude: data.longitude ?? null,
                    maritalStatus: data.maritalStatus || '',
                    gender: data.gender || '',
                    hasChildren: Boolean(data.hasChildren),
                    skinTone: data.skinTone || '',
                    bio: data.bio || '',
                    profilePicture: [],
                    imagesToRemove: []
                }));

                setExistingImages(Array.isArray(data.mediaUrls) ? data.mediaUrls : []);
            } catch (error) {
                if (error.name !== 'CanceledError') {
                    console.error('❌ Failed to load profile:', error);
                    setErrorMessage(t('editProfile.loadError', 'Impossible de charger votre profil.'));
                }
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
        return () => controller.abort();
    }, [authorizationHeader, t]);

    useEffect(() => {
        document.body.classList.add('edit-profile-body');

        return () => {
            document.body.classList.remove('edit-profile-body');
        };
    }, []);

    useEffect(() => {
        return () => {
            newImagePreviews.forEach((url) => URL.revokeObjectURL(url));
            if (locationTimerRef.current) window.clearTimeout(locationTimerRef.current);
        };
    }, [newImagePreviews]);

    const buildLocationFields = (address = {}) => {
        const city =
            address.city ||
            address.town ||
            address.village ||
            address.hamlet ||
            address.municipality ||
            address.suburb ||
            '';

        const state = address.state || address.region || address.state_district || '';
        const country = address.country || '';

        return {
            city,
            state,
            country,
            location: [city, state, country].filter(Boolean).join(', ')
        };
    };

    const reverseGeocode = async (latitude, longitude) => {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${latitude}&lon=${longitude}&accept-language=${currentLanguage}`
        );

        if (!response.ok) {
            throw new Error(`Reverse geocoding failed: ${response.status}`);
        }

        const data = await response.json();
        return buildLocationFields(data.address || {});
    };

    const detectLocation = () => {
        if (!navigator.geolocation) {
            setLocationMessage(
                t('editProfile.geolocationUnsupported', 'La géolocalisation n’est pas disponible sur cet appareil.')
            );
            return;
        }

        setLocationLoading(true);
        setLocationMessage('');
        setErrorMessage('');

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;

                try {
                    const locationFields = await reverseGeocode(latitude, longitude);
                    updateFormData({ ...locationFields, latitude, longitude });
                    setLocationMessage(t('editProfile.locationDetected', 'Localisation mise à jour.'));
                } catch (error) {
                    console.error('❌ Reverse geocoding failed:', error);
                    updateFormData({ latitude, longitude });
                    setLocationMessage(
                        t(
                            'editProfile.locationAddressError',
                            'Les coordonnées ont été trouvées, mais pas l’adresse. Vous pouvez saisir la ville manuellement.'
                        )
                    );
                } finally {
                    setLocationLoading(false);
                }
            },
            (error) => {
                console.warn('⚠️ Geolocation failed:', error);
                setLocationLoading(false);

                if (error.code === 1) {
                    setLocationMessage(
                        t(
                            'editProfile.locationPermissionError',
                            'Autorisez la localisation dans le navigateur ou entrez votre ville manuellement.'
                        )
                    );
                } else if (error.code === 3) {
                    setLocationMessage(
                        t(
                            'editProfile.locationTimeout',
                            'La détection prend trop de temps. Entrez votre ville, votre État et votre pays.'
                        )
                    );
                } else {
                    setLocationMessage(
                        t('editProfile.locationUnavailable', 'Votre position est actuellement indisponible.')
                    );
                }
            },
            {
                enableHighAccuracy: false,
                timeout: 30000,
                maximumAge: 600000
            }
        );
    };

    const geocodeManualLocation = (query) => {
        if (locationTimerRef.current) window.clearTimeout(locationTimerRef.current);
        if (query.trim().length < 5) return;

        locationTimerRef.current = window.setTimeout(async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&accept-language=${currentLanguage}&q=${encodeURIComponent(query.trim())}`
                );

                if (!response.ok) {
                    throw new Error(`Forward geocoding failed: ${response.status}`);
                }

                const results = await response.json();

                if (!results?.length) {
                    setLocationMessage(
                        t('editProfile.addressNotFound', 'Adresse introuvable. Ajoutez la ville, l’État et le pays.')
                    );
                    return;
                }

                const result = results[0];
                const locationFields = buildLocationFields(result.address || {});

                updateFormData({
                    city: locationFields.city,
                    state: locationFields.state,
                    country: locationFields.country,
                    latitude: Number(result.lat),
                    longitude: Number(result.lon)
                });

                setLocationMessage(
                    t('editProfile.addressCoordinatesFound', 'Les coordonnées de cette adresse ont été trouvées.')
                );
            } catch (error) {
                console.error('❌ Manual geocoding failed:', error);
            }
        }, 1000);
    };

    const handleChange = (event) => {
        const { name, value, type, checked, files } = event.target;

        setSuccessMessage('');
        setErrorMessage('');

        if (type === 'file') {
            const selectedFiles = Array.from(files || []);
            const totalPhotos = existingImages.length + selectedFiles.length;

            if (totalPhotos > MAX_PHOTOS) {
                setErrorMessage(
                    t('editProfile.maxPhotosError', `Vous pouvez avoir au maximum ${MAX_PHOTOS} photos.`)
                );
                return;
            }

            const invalidFile = selectedFiles.find(
                (file) =>
                    !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
                    file.size > MAX_PHOTO_SIZE
            );

            if (invalidFile) {
                setErrorMessage(
                    t(
                        'editProfile.photoFormatError',
                        'Utilisez uniquement JPG, PNG ou WEBP, avec 8 MB maximum par photo.'
                    )
                );
                return;
            }

            newImagePreviews.forEach((url) => URL.revokeObjectURL(url));
            updateFormData({ profilePicture: selectedFiles });
            setNewImagePreviews(selectedFiles.map((file) => URL.createObjectURL(file)));
            return;
        }

        if (type === 'checkbox') {
            updateFormData({ [name]: checked });
            return;
        }

        if (name === 'location') {
            updateFormData({
                location: value,
                city: '',
                state: '',
                country: '',
                latitude: null,
                longitude: null
            });
            setLocationMessage('');
            geocodeManualLocation(value);
            return;
        }

        updateFormData({ [name]: value });
    };

    const removeExistingImage = (index) => {
        const removedUrl = existingImages[index];

        setExistingImages((previous) =>
            previous.filter((_, currentIndex) => currentIndex !== index)
        );

        setFormData((previous) => ({
            ...previous,
            imagesToRemove: [...previous.imagesToRemove, removedUrl]
        }));
    };

    const removeNewImage = (index) => {
        URL.revokeObjectURL(newImagePreviews[index]);

        setNewImagePreviews((previous) =>
            previous.filter((_, currentIndex) => currentIndex !== index)
        );

        setFormData((previous) => ({
            ...previous,
            profilePicture: previous.profilePicture.filter(
                (_, currentIndex) => currentIndex !== index
            )
        }));
    };

    const validateForm = () => {
        if (
            !formData.name.trim() ||
            !formData.age ||
            Number(formData.age) < 18 ||
            !formData.location.trim() ||
            !formData.maritalStatus ||
            !formData.gender
        ) {
            setErrorMessage(t('editProfile.requiredFields', 'Remplissez tous les champs obligatoires.'));
            return false;
        }

        if (
            !Number.isFinite(Number(formData.latitude)) ||
            !Number.isFinite(Number(formData.longitude))
        ) {
            setErrorMessage(
                t(
                    'editProfile.locationCoordinatesRequired',
                    'Attendez que les coordonnées de votre localisation soient trouvées.'
                )
            );
            return false;
        }

        if (existingImages.length + formData.profilePicture.length === 0) {
            setErrorMessage(
                t('editProfile.photoRequired', 'Votre profil doit contenir au moins une photo.')
            );
            return false;
        }

        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;

        const payload = new FormData();

        Object.entries(formData).forEach(([key, value]) => {
            if (key === 'profilePicture') {
                value.forEach((file) => payload.append('profilePicture', file));
                return;
            }

            if (key === 'imagesToRemove') {
                value.forEach((url) => payload.append('imagesToRemove', url));
                return;
            }

            if (value !== null && value !== undefined) {
                payload.append(key, value);
            }
        });

        setSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/users/update`,
                payload,
                {
                    headers: {
                        Authorization: authorizationHeader,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/users/me`,
                { headers: { Authorization: authorizationHeader } }
            );

            localStorage.setItem('currentUser', JSON.stringify(response.data));
            window.dispatchEvent(new Event('refresh-profile'));

            setSuccessMessage(
                t('editProfile.success', 'Profil mis à jour avec succès.')
            );

            window.setTimeout(() => navigate('/suggestions'), 1200);
        } catch (error) {
            console.error('❌ Profile update failed:', error);
            setErrorMessage(
                error.response?.data?.message ||
                t('editProfile.updateError', 'Erreur lors de la mise à jour du profil.')
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <main className="edit-profile-page">
                <div className="edit-profile-state">
                    <div className="edit-profile-spinner" />
                    <p>{t('common.loading', 'Chargement...')}</p>
                </div>
            </main>
        );
    }

    return (
        <main className="edit-profile-page">
            <section className="edit-profile-container">
                <header className="edit-profile-header">
                    <span className="edit-profile-header-icon"><FaUser /></span>
                    <div>
                        <h1>{t('editProfile.title', 'Modifier le profil')}</h1>
                        <p>{t('editProfile.subtitle', 'Mettez à jour vos informations et vos photos.')}</p>
                    </div>
                </header>

                <form className="edit-profile-form" onSubmit={handleSubmit}>
                    {errorMessage && (
                        <div className="edit-profile-message edit-profile-message--error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {successMessage && (
                        <div className="edit-profile-message edit-profile-message--success" role="status">
                            <FaCheckCircle /> {successMessage}
                        </div>
                    )}

                    <section className="edit-profile-section">
                        <SectionTitle
                            number="01"
                            title={t('editProfile.personalInformation', 'Informations personnelles')}
                            subtitle={t('editProfile.personalInformationHint', 'Les informations principales de votre profil.')}
                        />

                        <div className="edit-profile-grid">
                            <Field label={t('register.fullName', 'Nom complet')}>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                            </Field>

                            <Field label={t('register.age', 'Âge')}>
                                <input type="number" name="age" value={formData.age} onChange={handleChange} min="18" max="100" required />
                            </Field>

                            <Field label={t('register.gender', 'Genre')}>
                                <select name="gender" value={formData.gender} onChange={handleChange} required>
                                    <option value="">{t('register.selectOption', 'Sélectionner')}</option>
                                    <option value="homme">{t('register.male', 'Homme')}</option>
                                    <option value="femme">{t('register.female', 'Femme')}</option>
                                </select>
                            </Field>

                            <Field label={t('register.maritalStatus', 'Situation matrimoniale')}>
                                <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} required>
                                    <option value="">{t('register.selectOption', 'Sélectionner')}</option>
                                    <option value="célibataire">{t('register.single', 'Célibataire')}</option>
                                    <option value="marié">{t('register.married', 'Marié')}</option>
                                    <option value="divorcé">{t('register.divorced', 'Divorcé')}</option>
                                </select>
                            </Field>

                            <Field label={t('editProfile.skinTone', 'Teint')}>
                                <select name="skinTone" value={formData.skinTone} onChange={handleChange}>
                                    <option value="">{t('register.selectOption', 'Sélectionner')}</option>
                                    <option value="clair">{t('editProfile.skinLight', 'Clair')}</option>
                                    <option value="moyen">{t('editProfile.skinMedium', 'Moyen')}</option>
                                    <option value="foncé">{t('editProfile.skinDark', 'Foncé')}</option>
                                </select>
                            </Field>

                            <label className="edit-profile-checkbox-card">
                                <input type="checkbox" name="hasChildren" checked={formData.hasChildren} onChange={handleChange} />
                                <span className="edit-profile-checkbox-icon"><FaCheck /></span>
                                <span>
                                    <strong>{t('register.hasChildren', 'J’ai des enfants')}</strong>
                                    <small>{t('editProfile.childrenHint', 'Cette information sera affichée sur votre profil.')}</small>
                                </span>
                                <FaChild />
                            </label>
                        </div>
                    </section>

                    <section className="edit-profile-section">
                        <SectionTitle
                            number="02"
                            title={t('editProfile.locationSection', 'Localisation')}
                            subtitle={t('editProfile.locationSectionHint', 'Utilisée pour calculer la distance entre les profils.')}
                        />

                        <div className="edit-profile-location">
                            <FaMapMarkerAlt />
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder={t('register.location', 'Ville, État, pays')}
                                required
                            />
                            <button type="button" onClick={detectLocation} disabled={locationLoading}>
                                {locationLoading
                                    ? t('editProfile.detecting', 'Détection...')
                                    : t('editProfile.detectLocation', 'Détecter')}
                            </button>
                        </div>

                        {locationMessage && (
                            <div className="edit-profile-location-message">{locationMessage}</div>
                        )}

                        <div className="edit-profile-location-summary">
                            <SummaryItem label={t('editProfile.city', 'Ville')} value={formData.city || '—'} />
                            <SummaryItem label={t('editProfile.state', 'État')} value={formData.state || '—'} />
                            <SummaryItem label={t('editProfile.country', 'Pays')} value={formData.country || '—'} />
                        </div>
                    </section>

                    <section className="edit-profile-section">
                        <SectionTitle
                            number="03"
                            title={t('editProfile.aboutSection', 'À propos de vous')}
                            subtitle={t('editProfile.aboutSectionHint', 'Présentez-vous avec vos propres mots.')}
                        />

                        <div className="edit-profile-bio-field">
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                placeholder={t('register.bio', 'Votre biographie')}
                                maxLength="1000"
                                rows="5"
                            />
                            <span className={formData.bio.length > 900 ? 'is-near-limit' : ''}>
                                {formData.bio.length}/1000
                            </span>
                        </div>
                    </section>

                    <section className="edit-profile-section">
                        <SectionTitle
                            number="04"
                            title={t('editProfile.photosSection', 'Photos')}
                            subtitle={t('editProfile.photosSectionHint', `Ajoutez jusqu’à ${MAX_PHOTOS} photos.`)}
                        />

                        <label className="edit-profile-upload">
                            <FaCamera />
                            <span>
                                <strong>{t('editProfile.choosePhoto', 'Choisir des photos')}</strong>
                                <small>{t('editProfile.photoFormats', 'JPG, PNG ou WEBP — 8 MB maximum.')}</small>
                            </span>
                            <input
                                type="file"
                                name="profilePicture"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                hidden
                                onChange={handleChange}
                            />
                        </label>

                        <div className="edit-profile-photo-count">
                            {existingImages.length + newImagePreviews.length}/{MAX_PHOTOS}
                        </div>

                        <div className="edit-profile-photos">
                            {existingImages.map((url, index) => (
                                <PhotoCard
                                    key={`existing-${url}`}
                                    src={url}
                                    index={index}
                                    isMain={index === 0}
                                    onRemove={() => removeExistingImage(index)}
                                    t={t}
                                />
                            ))}

                            {newImagePreviews.map((url, index) => (
                                <PhotoCard
                                    key={`new-${url}`}
                                    src={url}
                                    index={existingImages.length + index}
                                    isMain={existingImages.length === 0 && index === 0}
                                    onRemove={() => removeNewImage(index)}
                                    t={t}
                                    isNew
                                />
                            ))}
                        </div>
                    </section>

                    <button type="submit" className="edit-profile-save" disabled={saving}>
                        <FaSave />
                        {saving
                            ? t('editProfile.saving', 'Enregistrement...')
                            : t('editProfile.save', 'Enregistrer les modifications')}
                    </button>
                </form>
            </section>
        </main>
    );
}

function SectionTitle({ number, title, subtitle }) {
    return (
        <div className="edit-profile-section-title">
            <span>{number}</span>
            <div>
                <h2>{title}</h2>
                <p>{subtitle}</p>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="edit-profile-field">
            <span>{label}</span>
            {children}
        </label>
    );
}

function SummaryItem({ label, value }) {
    return (
        <div className="edit-profile-summary-item">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

function PhotoCard({ src, index, isMain, isNew, onRemove, t }) {
    return (
        <div className="edit-profile-photo-card">
            <img
                src={src}
                alt={`${t('editProfile.photo', 'Photo')} ${index + 1}`}
                onError={(event) => {
                    event.currentTarget.src = '/default-profile.jpg';
                }}
            />

            {isMain && (
                <span className="edit-profile-main-photo">
                    {t('editProfile.mainPhoto', 'Principale')}
                </span>
            )}

            {isNew && (
                <span className="edit-profile-new-photo">
                    {t('editProfile.newPhoto', 'Nouvelle')}
                </span>
            )}

            <button
                type="button"
                className="edit-profile-delete"
                onClick={onRemove}
                aria-label={t('editProfile.deletePhoto', 'Supprimer la photo')}
            >
                <FaTrash />
            </button>
        </div>
    );
}

export default EditProfile;